import type { AtlasEdge, AtlasNode, Confidence, ImpactPath, ScenarioType } from './types';

const OUTAGE_REVERSED = new Set([
  'DEPENDS_ON', 'AUTHENTICATES_VIA', 'TRUSTS_IDENTITY_FROM', 'HOSTED_ON',
  'PROVIDED_BY', 'REQUIRES_ROLE', 'STORES_DATA_IN', 'HAS_ACCESS_TO',
  'SENDS_DATA_TO', 'CONNECTS_TO',
]);

const BREACH_FORWARD = new Set(['HAS_ACCESS_TO', 'SENDS_DATA_TO', 'CONNECTS_TO']);
const BREACH_REVERSED = new Set(['STORES_DATA_IN', 'AUTHENTICATES_VIA', 'TRUSTS_IDENTITY_FROM']);
const CONFIDENCE_RANK: Record<Confidence, number> = { unknown: 0, inferred: 1, reported: 2, confirmed: 3 };

function lowerConfidence(a: Confidence, b: Confidence): Confidence {
  return CONFIDENCE_RANK[a] <= CONFIDENCE_RANK[b] ? a : b;
}
function strengthFactor(edge: AtlasEdge): number {
  if (edge.strength === 'hard') return 1;
  if (edge.strength === 'degraded') return 0.6;
  return 0.2;
}

function severityFor(node: AtlasNode, factor: number): ImpactPath['severity'] {
  const score = node.criticality * factor;
  if (score >= 4) return 'critical';
  if (score >= 2.8) return 'high';
  if (score >= 1.5) return 'moderate';
  return 'low';
}

interface QueueItem {
  nodeId: string;
  edgeIds: string[];
  nodeIds: string[];
  factor: number;
  time: number;
  confidence: Confidence;
}

export function analyzeImpact(
  nodes: AtlasNode[],
  edges: AtlasEdge[],
  originNodeId: string,
  type: ScenarioType,
): ImpactPath[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const queue: QueueItem[] = [{
    nodeId: originNodeId,
    edgeIds: [],
    nodeIds: [originNodeId],
    factor: 1,
    time: 0,
    confidence: 'confirmed',
  }];
  const best = new Map<string, QueueItem>();
  best.set(originNodeId, queue[0]);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const candidates: { edge: AtlasEdge; nextId: string }[] = [];

    for (const edge of edges) {
      if (type === 'outage' && OUTAGE_REVERSED.has(edge.type) && edge.targetId === current.nodeId) {
        candidates.push({ edge, nextId: edge.sourceId });
      }
      if (type === 'breach') {
        if (BREACH_FORWARD.has(edge.type) && edge.sourceId === current.nodeId) {
          candidates.push({ edge, nextId: edge.targetId });
        }
        if (BREACH_REVERSED.has(edge.type) && edge.targetId === current.nodeId) {
          candidates.push({ edge, nextId: edge.sourceId });
        }
      }
    }

    for (const { edge, nextId } of candidates) {
      if (current.nodeIds.includes(nextId)) continue;
      const candidate: QueueItem = {
        nodeId: nextId,
        edgeIds: [...current.edgeIds, edge.id],
        nodeIds: [...current.nodeIds, nextId],
        factor: current.factor * strengthFactor(edge),
        time: current.time + Math.max(0, edge.toleranceMinutes),
        confidence: lowerConfidence(current.confidence, edge.confidence),
      };
      const existing = best.get(nextId);
      if (!existing || candidate.factor > existing.factor || candidate.time < existing.time) {
        best.set(nextId, candidate);
        queue.push(candidate);
      }
    }
  }

  return [...best.values()]
    .filter((item) => item.nodeId !== originNodeId)
    .map((item) => ({
      nodeId: item.nodeId,
      edgeIds: item.edgeIds,
      nodeIds: item.nodeIds,
      severity: severityFor(byId.get(item.nodeId)!, item.factor),
      timeToImpact: item.time,
      confidence: item.confidence,
    }))
    .sort((a, b) => {
      const severityRank = { critical: 4, high: 3, moderate: 2, low: 1 };
      return severityRank[b.severity] - severityRank[a.severity] || a.timeToImpact - b.timeToImpact;
    });
}

export function structuralWarnings(nodes: AtlasNode[], edges: AtlasEdge[]): string[] {
  const warnings: string[] = [];
  const connected = new Set(edges.flatMap((edge) => [edge.sourceId, edge.targetId]));
  const orphans = nodes.filter((node) => !connected.has(node.id));
  const criticalWithoutFallback = nodes.filter((node) =>
    node.type === 'capability' && node.criticality >= 4 &&
    !edges.some((edge) => edge.sourceId === node.id && edge.type === 'FALLS_BACK_TO'),
  );
  const missingOwners = nodes.filter((node) => !node.owner.trim());
  const criticalWithoutRto = nodes.filter((node) => node.criticality >= 4 && node.rtoMinutes === undefined);
  const missingEvidence = nodes.filter((node) => node.sourceRefs.length === 0);
  const lowConfidenceRelationships = edges.filter((edge) => edge.confidence === 'unknown');
  if (orphans.length) warnings.push(`${orphans.length} orphaned ${orphans.length === 1 ? 'entity' : 'entities'}`);
  if (criticalWithoutFallback.length) warnings.push(`${criticalWithoutFallback.length} critical capabilities without fallback`);
  if (missingOwners.length) warnings.push(`${missingOwners.length} entities without owners`);
  if (criticalWithoutRto.length) warnings.push(`${criticalWithoutRto.length} critical ${criticalWithoutRto.length === 1 ? 'entity' : 'entities'} without ${criticalWithoutRto.length === 1 ? 'an RTO' : 'RTOs'}`);
  if (missingEvidence.length) warnings.push(`${missingEvidence.length} ${missingEvidence.length === 1 ? 'entity' : 'entities'} without source evidence`);
  if (lowConfidenceRelationships.length) warnings.push(`${lowConfidenceRelationships.length} ${lowConfidenceRelationships.length === 1 ? 'relationship' : 'relationships'} with unknown confidence`);
  return warnings;
}
